import {ApolloError, ServerError, useQuery} from "@apollo/client";
import log from "loglevel";
import _ from "lodash";
import {createContext, useContext, useEffect, useRef, useState, useMemo} from "react";
import {
  Manifest,
  ManifestFilter,
  ManifestSortInput,
  SearchableSortDirection,
  useGetEtaQuery,
  useSearchManifestDetailsQuery
} from "../../../generated/graphql";
import {Constants, TenantPreferences, UserPreferences} from "../../../constants/constants";
import {GraphQLErrors} from "@apollo/client/errors";
import {decorateManifestETA} from "../details/utils/ManifestDetails.utils";
import {FeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {AppToaster} from "../../../utils/toasters/toaster";
import {Intent} from "@blueprintjs/core";
import {extractJsonPref, extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";
import {
  showQualifiedDriversWarningToast,
  SkillsRequirementsBody
} from "../../../utils/toasters/qualified-drivers-toaster/QualifiedDriversToaster";
import * as React from "react";
import {TManifestEnhanced} from "../../common/types";
import {useAssignmentState} from "../../../providers/AssignmentProdiver";
import {useAssignmentViewState} from "../../../providers/AssignmentViewProvider";
import {toServerRankSortAscending} from "../../../utils/color-indicator/color-indicator.utils";
import {IManifestCardConfigurationsV2} from "../card-configuration/v2/types/manifest-card-configurations.type";
import {buildManifestQuery} from "../../../utils/dynamic-query/manifest-query-builder";
import {getDefaultCardConfigurations} from "../card-configuration/v2/constants/constants";

// DP-3214: `searchNestedEntity.js` rewrites `_` to `.` in a sort field, so this reaches
// OpenSearch as a nested sort on `stops.indicatorRank`. Renaming it needs the resolver checked.
export const MANIFEST_COLOR_SORT_FIELD = "stops_indicatorRank";

/**
 * DP-3214: the colour clause for the manifest query, or nothing.
 *
 * With backend colours the worker stamps `indicatorRank` on every manifest stop, so the colour
 * ordering belongs in the query rather than in a client-side re-sort of whichever page happens
 * to be loaded. Both flags have to be on: `colorSort` is what puts the control in the sort menu
 * at all, and `backendColorRules` is what makes the stamps exist to sort by.
 *
 * DP-3213 gives that nested sort `mode: min`, which is what makes descending the true reverse
 * of ascending — without it descending ranks a manifest by its LEAST important stop.
 *
 * DP-3216: the direction sent is the OPPOSITE of the user's choice. Rank 1 is the most important
 * colour, so ascending by rank number is descending by importance — the reverse of what the
 * client path means by ascending.
 *
 * Exported for tests: the rest of this provider needs a live Apollo client to exercise.
 */
export const buildManifestColorSort = ({
  backendColorRulesEnabled,
  colorSortEnabled,
  sortByColor
}: {
  backendColorRulesEnabled?: boolean;
  colorSortEnabled?: boolean;
  sortByColor?: {enable: boolean; direction: SearchableSortDirection};
}): ManifestSortInput[] =>
  backendColorRulesEnabled && colorSortEnabled && sortByColor?.enable
    ? [
        {
          field: MANIFEST_COLOR_SORT_FIELD,
          // DP-3216: inverted on purpose — see toServerRankSortAscending.
          direction: toServerRankSortAscending(sortByColor.direction === SearchableSortDirection.Asc)
            ? SearchableSortDirection.Asc
            : SearchableSortDirection.Desc
        }
      ]
    : [];

type ManifestDataProviderProps = {
  children: JSX.Element[] | JSX.Element;
  filter?: ManifestFilter;
  sort?: ManifestSortInput;
  skip?: boolean;
  isReassign?: boolean;
  /** Extra field paths to include in the dynamic query (e.g. IP needs stop details for the map grid). */
  additionalFields?: string[];
};

export type ManifestDataState = {
  data: TManifestEnhanced[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  setPage(page: number): void;
  setPageSize?: (pageSize: number) => void;
  hasBadData: boolean;
  /** True while the initial ETA fetch has not yet completed (ETA enabled but no data yet). */
  etaLoading: boolean;
};

const initialManifestDataState = {
  data: undefined,
  loading: true,
  error: undefined,
  total: 0,
  page: 0,
  pageSize: Constants.DEFAULT_MANIFEST_PAGE_SIZE,
  pageCount: 1,
  setPage: () => {
    /*NOOP*/
  },
  hasBadData: false,
  etaLoading: false
};

export const getArrayOfErrorIndexes = (errors: GraphQLErrors | undefined) => {
  return errors
    ? errors.map((error) => {
        return Number(error.path?.at(2));
      })
    : [];
};

export const filterNonErrManifests = (manifests: Manifest[], indexes: number[]) => {
  if (indexes.length === 0) return manifests;
  return manifests.filter((_, index) => !indexes.includes(index));
};

const CODE_SIZE_LIMIT_REACHED_MSG =
  "The amount of data in the selected number of manifests exceeds our capacity. Please reduce the number of manifests per page at the bottom of the screen then refresh the browser.";

export const ManifestDataContext = createContext<ManifestDataState>(initialManifestDataState);

const ManifestDataProvider = ({
  children,
  filter,
  sort,
  skip,
  isReassign,
  additionalFields
}: ManifestDataProviderProps) => {
  const {
    etas: isETAEnabled,
    skills: isSkillsEnabled,
    lessStopDataPoints: isLessStopDataPointsEnabled,
    colorSort: isColorSortEnabled,
    backendColorRules: isBackendColorRulesEnabled
  } = useContext(FeatureFlagContext);
  const {userPreferences, tenantPreferences} = usePreferenceContext();

  const [manifestData, setManifestData] = useState<Manifest[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(Constants.DEFAULT_MANIFEST_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);
  const [manifestsLoading, setManifestsLoading] = useState(false);

  const [extendedSort, setExtendedSort] = useState<ManifestSortInput[]>([]);
  const [manifestSort, setManifestSort] = useState<ManifestSortInput | undefined>(undefined);
  const [manifestFilter, setManifestFilter] = useState<ManifestFilter | undefined>(undefined);
  const [hasBadData, setHasBadData] = useState(false);
  const [state, setState] = useState<ManifestDataState>({
    ...initialManifestDataState
  });

  // DP-3176: cache the last successful ETA items so they can be reused when
  // etaResult.data is transiently unavailable (e.g. Apollo query transitioning
  // during a detail-panel close). This prevents decorateManifestETA from being
  // called with undefined, which would strip ETA fields and cause a colour flicker.
  const lastEtaItemsRef = useRef<any[]>([]);

  const {selectedStops, selectedJobs, jobsRequirements, reassignJobsRequirements} = useAssignmentState();
  const {sortByColor} = useAssignmentViewState();
  const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;
  const isSkillsBasedRequirements = extractSimplePref(
    tenantPreferences,
    TenantPreferences.skillsBasedRequirements,
    false
  ).value as boolean;

  // DP-3214: built here rather than at the three ManifestDriverPanel call sites because each of
  // them mounts its own ManifestDataProvider beneath the app-wide AssignmentViewProvider, so
  // reading the state once here covers every one of them.
  const colorSort = buildManifestColorSort({
    backendColorRulesEnabled: isBackendColorRulesEnabled,
    colorSortEnabled: isColorSortEnabled,
    sortByColor
  });

  const queryVariables = {
    filter: filter,
    sort: [
      // Colour leads, and the property sort stays behind it as the tie-breaker. That mirrors
      // the client path it replaces: Array.prototype.sort is stable, so re-sorting an
      // already-property-sorted page by colour left same-colour manifests in property order.
      ...colorSort,
      ...extendedSort,
      manifestSort ?? {},
      {
        field: "manifestDate",
        direction: SearchableSortDirection.Asc
      },
      {
        field: "manifestDriverId",
        direction: SearchableSortDirection.Asc
      }
    ],
    offset: page * pageSize,
    limit: pageSize
  };

  const queryOptions = {
    pollInterval: Constants.GENERAL_POLLING_INTERVAL,
    errorPolicy: "all" as const
  };

  const cardConfigurationsV2: IManifestCardConfigurationsV2 = useMemo(
    () =>
      extractJsonPref(
        userPreferences,
        UserPreferences.manifestCardConfigurations,
        getDefaultCardConfigurations(isUseETAService)
      ).value,
    [userPreferences, isUseETAService]
  );

  // Determine query strategy based on feature flag and configuration
  const queryStrategy = useMemo(() => {
    if (!isLessStopDataPointsEnabled) {
      log.debug("[ManifestDataProvider] Using DETAILED query");
      return "detailed";
    }
    log.debug("[ManifestDataProvider] Using DYNAMIC query");
    return "dynamic";
  }, [isLessStopDataPointsEnabled]);

  // Use detailed query (SearchManifestDetails)
  const manifestResultDetailed = useSearchManifestDetailsQuery({
    variables: queryVariables,
    ...queryOptions,
    skip: skip || queryStrategy !== "detailed"
  });

  // Use dynamic query
  const manifestResultDynamic = useQuery(buildManifestQuery(cardConfigurationsV2, additionalFields), {
    variables: queryVariables,
    ...queryOptions,
    skip: skip || queryStrategy !== "dynamic"
  });

  const manifestResult = queryStrategy === "detailed" ? manifestResultDetailed : manifestResultDynamic;

  const etaResult = useGetEtaQuery({
    variables: {
      manifestDriverIds: manifestData.map((manifest) => manifest.manifestDriverId)
    },
    pollInterval: Constants.GENERAL_POLLING_INTERVAL,
    errorPolicy: "all",
    skip: skip || !isETAEnabled || manifestData.length === 0 || manifestsLoading || !isUseETAService
  });

  useEffect(() => {
    if (JSON.stringify(filter) !== JSON.stringify(manifestFilter)) {
      setManifestFilter(filter);
      setPage(0);
      setTotalRows(0);
    }
  }, [filter, manifestFilter]);

  useEffect(() => {
    if (JSON.stringify(sort) !== JSON.stringify(manifestSort)) {
      const extendSort = [];
      if (sort?.field === "driver.code.keyword") {
        extendSort.push({
          field: "driver.codeNumeric",
          direction: sort.direction
        });
      }
      setExtendedSort(extendSort);
      setManifestSort(sort);
      setPage(0);
      setTotalRows(0);
    }
  }, [sort, manifestSort]);

  // DP-3214: toggling or reversing the colour sort reorders the whole result set, so the page
  // the user is on no longer means anything — go back to the first, exactly as the property
  // sort does above. `totalRows` is deliberately left alone: a reorder does not change how many
  // manifests match, and zeroing it would blank the pager for a frame.
  const colorSortKey = JSON.stringify(colorSort);
  const previousColorSortKey = useRef(colorSortKey);
  useEffect(() => {
    if (previousColorSortKey.current !== colorSortKey) {
      previousColorSortKey.current = colorSortKey;
      setPage(0);
    }
  }, [colorSortKey]);

  useEffect(() => {
    if (page + 1 > Math.ceil(totalRows / pageSize)) {
      setPage(0);
    }
  }, [page, pageSize, totalRows]);

  useEffect(() => {
    setManifestsLoading(manifestResult.loading);
    if (skip || manifestResult.loading || etaResult.loading) return;
    if (manifestResult.data?.searchManifests) {
      const hasBadData =
        manifestResult.error?.graphQLErrors.some((error) =>
          Constants.REGEX.BAD_DATA_RESPONSE_REGEX.test(error.message)
        ) ?? false;
      setHasBadData(hasBadData);

      let newManifestData;
      if (hasBadData) {
        newManifestData = filterNonErrManifests(
          manifestResult.data.searchManifests.items as Manifest[],
          getArrayOfErrorIndexes(manifestResult.error?.graphQLErrors)
        );
      } else {
        newManifestData = manifestResult.data.searchManifests.items as Manifest[];
      }
      if (isETAEnabled && isUseETAService) {
        // DP-3176: use fresh ETA data when available, otherwise fall back to
        // the last cached items. This keeps ETA decoration stable during
        // transient query gaps (e.g. detail-panel close) and avoids a colour
        // flicker caused by decorateManifestETA receiving undefined.
        const freshEtaItems = etaResult?.data?.getETA?.items;
        if (freshEtaItems) {
          lastEtaItemsRef.current = freshEtaItems;
        }
        const etaData = freshEtaItems ?? lastEtaItemsRef.current;
        newManifestData = newManifestData.map((manifest) => {
          const manifestETA = etaData.find((item: any) => item.manifestDriverId === manifest.manifestDriverId);
          return decorateManifestETA(manifest, manifestETA);
        });
      }

      const currentRequirements = isReassign ? reassignJobsRequirements : jobsRequirements;
      if (newManifestData.length === 0 && currentRequirements.size > 0) {
        const isSkillsBased = isSkillsEnabled && isSkillsBasedRequirements;
        const isMultipleJobsSelected =
          selectedJobs.length > 1 || new Set(selectedStops.map((stop) => stop.job.jobNumber)).size > 1;
        if (isSkillsBased) {
          showQualifiedDriversWarningToast({
            customBody: (
              <SkillsRequirementsBody
                isMultipleJobsSelected={isMultipleJobsSelected}
                listOfSkills={[...currentRequirements.values()].map((skill) => skill.name)}
              />
            )
          });
        } else {
          showQualifiedDriversWarningToast({});
        }
      }

      if (!_.isEqual(manifestData, newManifestData)) {
        setManifestData(newManifestData);
      }

      const total = manifestResult.data.searchManifests.total ?? 0;
      if (totalRows !== total) {
        setTotalRows(total);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    skip,
    etaResult.data?.getETA?.items,
    etaResult.loading,
    isETAEnabled,
    manifestResult.data,
    manifestResult.loading,
    isUseETAService
  ]);

  useEffect(() => {
    if (manifestResult.error) {
      log.error("[ERROR] - ManifestDataContext", {
        message: manifestResult.error.message,
        hasData: manifestResult.data !== undefined,
        networkStatus: manifestResult.networkStatus,
        raw: JSON.stringify(manifestResult.error)
      });

      const isCodeSizeLimitReachedErr =
        manifestResult.error?.graphQLErrors.some((error) =>
          Constants.REGEX.CODE_SIZE_LIMIT_REACHED_REGEX.test(error.message)
        ) ?? false;

      if (isCodeSizeLimitReachedErr) {
        AppToaster.show({
          intent: Intent.WARNING,
          icon: "warning-sign",
          timeout: 10000,
          message: CODE_SIZE_LIMIT_REACHED_MSG
        });
        setManifestData([]);
      }

      //This will rety at the ApolloLink level
      if (manifestResult.error.networkError && (manifestResult.error.networkError as ServerError).statusCode !== 401) {
        setState({
          ...initialManifestDataState,
          loading: manifestResult.loading,
          error: manifestResult.error
        });
      }
    }
  }, [manifestResult.data, manifestResult.error, manifestResult.loading, manifestResult.networkStatus]);

  // DP-3176: true while ETA is enabled but the first ETA response has not arrived yet.
  // Consumers use this to defer client-side colour processing so the engine runs only
  // once ETAs are present — avoiding a visible flicker on panel open.
  // Uses lastEtaItemsRef (which caches successful ETA responses) so transient query
  // skips (e.g. panel close) don't flip etaLoading back to true.
  const etaLoading = Boolean(
    isETAEnabled && isUseETAService && !etaResult.data && lastEtaItemsRef.current.length === 0
  );

  useEffect(() => {
    const updatedState = {
      ...initialManifestDataState,
      loading: manifestsLoading,
      data: manifestData as TManifestEnhanced[],
      page: page,
      pageSize: pageSize,
      pageCount: Math.ceil(totalRows / pageSize),
      total: totalRows,
      setPage: setPage,
      setPageSize: setPageSize,
      hasBadData: hasBadData,
      etaLoading: etaLoading
    };
    //log.debug("[DEBUG] - ManifestDataContext updated", updatedState);
    setState(updatedState);
  }, [manifestData, totalRows, pageSize, manifestsLoading, page, hasBadData, etaLoading]);

  return <ManifestDataContext.Provider value={state}>{children}</ManifestDataContext.Provider>;
};

export const useManifestDataContext = () => {
  const context = useContext(ManifestDataContext);
  if (context === undefined) {
    throw new Error("useManifestDataContext must be used within a ManifestDataProvider");
  }
  return context;
};

export default ManifestDataProvider;
